import { findClass, findSubclass, findVehicle, getItem, allSets, type Deed, type Item, type Vehicle } from '@bratva/content';
import {
  CARRY_ON_FOOT,
  NO_CLASS_BONUS,
  combineClassBonuses,
  combineGear,
  companionSlots,
  statWithGear,
  type ClassBonus,
  type Doer,
  type GearEffects,
} from '@bratva/rules';
import type { SaveDoc } from '@bratva/state';

/**
 * Пацан целиком: статы с надетым шмотом, собранные сеты и бонусы класса.
 * Всё в одном месте, чтобы «почему шанс 62%» имело один ответ, а не три.
 */

export interface Character {
  doer: Doer;
  gear: GearEffects;
  klass: ClassBonus;
  worn: Item[];
  vehicle: Vehicle | null;
}

export function character(save: SaveDoc): Character {
  const worn = Object.values(save.equipment)
    .filter((def): def is string => Boolean(def))
    .map((def) => getItem(def));

  const gear = combineGear(worn, allSets());
  const klass = combineClassBonuses(
    [findClass(save.player.classId), findSubclass(save.player.classId, save.player.subclassId)]
      .filter((entry) => entry !== null)
      .map((entry) => ({
        branch: entry!.branch,
        payoutFactor: entry!.payoutFactor,
        minutesFactor: entry!.minutesFactor,
        heatFactor: entry!.heatFactor,
        statBonus: entry!.statBonus,
        healthBonus: entry!.healthBonus,
        companionSlots: entry!.companionSlots,
      })),
  );

  const vehicle = findVehicle(save.activeVehicle);
  const p = save.player;
  const withClass = (stat: 'sila' | 'smekalochka' | 'obayanie' | 'chotkost' | 'avtoritet'): number =>
    p[stat] + (klass.statBonus?.[stat] ?? 0);

  return {
    worn,
    gear,
    vehicle,
    klass: save.player.classId ? klass : { ...NO_CLASS_BONUS, statBonus: {}, healthBonus: 0, companionSlots: 0 },
    doer: {
      sila: statWithGear(withClass('sila'), 'sila', gear),
      smekalochka: statWithGear(withClass('smekalochka'), 'smekalochka', gear),
      obayanie: statWithGear(withClass('obayanie'), 'obayanie', gear),
      chotkost: statWithGear(withClass('chotkost'), 'chotkost', gear),
      avtoritet: statWithGear(withClass('avtoritet') + (vehicle?.avtoritet ?? 0), 'avtoritet', gear),
      stress: p.stress,
    },
  };
}

/** Профильное ли это дело для класса: по нему множители и время. */
export function isOwnBranch(save: SaveDoc, deed: Deed): boolean {
  const { klass } = character(save);
  return Boolean(deed.branch && klass.branch === deed.branch);
}

export function deedMinutes(save: SaveDoc, deed: Deed): number {
  const { gear, klass } = character(save);
  const classFactor = isOwnBranch(save, deed) ? klass.minutesFactor : 1;
  return Math.max(1, Math.round(deed.minutes * gear.timeFactor * classFactor));
}

export function deedPayoutFactor(save: SaveDoc, deed: Deed): number {
  const { klass } = character(save);
  return isOwnBranch(save, deed) ? klass.payoutFactor : 1;
}

export function deedHeatFactor(save: SaveDoc, deed: Deed): number {
  const { gear, klass } = character(save);
  const classFactor = isOwnBranch(save, deed) ? klass.heatFactor : 1;
  return gear.heatFactor * classFactor;
}

/** Множитель времени ходьбы: сет «Адидас» экономит пятую часть дороги. */
export function walkFactor(save: SaveDoc): number {
  return character(save).gear.walkFactor;
}

/** Комиссия приёмки с учётом сета «Барыга» и цены сбыта с «Малинового». */
export function sellTerms(save: SaveDoc, commission: number): { commission: number; priceFactor: number } {
  const { gear } = character(save);
  return {
    commission: Math.max(0, commission - gear.commissionRelief),
    priceFactor: gear.sellFactor,
  };
}

/** Стресс за дело с учётом сета «Реальный пацан». */
export function stressFor(save: SaveDoc, stress: number): number {
  return Math.max(0, stress - character(save).gear.stressRelief);
}

/** Сколько хабара унесёшь: в руках две единицы, дальше по багажнику. */
export function carryCapacity(save: SaveDoc): number {
  return character(save).vehicle?.capacity ?? CARRY_ON_FOOT;
}

/** Множитель времени дороги между зонами. */
export function travelFactor(save: SaveDoc): number {
  return character(save).vehicle?.travelFactor ?? 1;
}

/** Приметность: складывается из тачки и малинового пиджака. */
export function flashiness(save: SaveDoc): number {
  const { vehicle, gear } = character(save);
  const malinovy = gear.sets.some((set) => set.def === 'malinovy') ? 2 : 0;
  return (vehicle?.flashiness ?? 0) + malinovy;
}

export function companionSlotsFor(save: SaveDoc): number {
  const { klass, doer } = character(save);
  return companionSlots(doer.obayanie, klass.companionSlots ?? 0);
}
